// ============================================
// PANELFILTROSEXPEDIENTES.JS — los filtros, guardados bajo un boton (EXPEDIENTES-RESUMEN FRENTE D)
// ============================================
// EL PROBLEMA (2026-09-17)
// La cabecera de Expedientes gastaba media pantalla antes de enseñar un solo expediente: cuatro
// tarjetas-resumen, la fila de pestañas y TRES filas de filtros siempre desplegadas. En un portatil
// quedaban dos expedientes a la vista; en el celular, ninguno.
//
// LA SOLUCION: los filtros no desaparecen — no se quita NI UNA opcion — pero dejan de estar todos
// a la vez en pantalla. Viven detras de un boton "Filtros" con el contador de cuantos hay puestos,
// y lo que hay puesto se lee de un vistazo en las etiquetas de abajo (que solo existen si hay algo
// puesto). Sin filtros, el bloque mide la mitad.
//
// DONDE SE ABRE
//   - Celular: HOJA INFERIOR (sube desde abajo). Es donde llega el pulgar y no tapa el contexto.
//   - Escritorio: panel colgado del boton.
// Los dos por portal, para no depender del apilamiento del contenedor: la barra del megamenu ya
// enseño (Sesion #22) que subir z-index no arregla un stacking context ajeno.
//
// LOS FILTROS SE APLICAN AL INSTANTE, no al cerrar. Por eso el boton del pie dice "Ver N
// expedientes" con el conteo EN VIVO: no es un "aplicar", es un "ya esta, llevame a verlos".
//
// El alto de la hoja se expresa con `calc(var(--beep-vh) * N)` y nunca con `vh` pelado (regla de
// CLAUDE.md nacida del bug de PERDIDA DE DATOS del 2026-09-12; lo vigila portero-modales.js).

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, Bookmark, Check } from 'lucide-react';
import { MAX_VISTAS, vistaActiva } from '../../utils/vistasGuardadas';

const FF = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const COLOR_OSCURO  = '#270f2b';
const COLOR_GRIS    = '#5e666e';
const COLOR_GRIS_CL = '#9aa0a6';
const COLOR_BORDE   = '#e5e7eb';
const COLOR_AZUL    = '#185FA5';
const COLOR_LIMA    = '#94b803';

const ANCHO_PANEL = 480;

export default function PanelFiltrosExpedientes({
  abierto, onCerrar, esCelular, anclaRef,
  grupos,                 // [{ id, titulo, contenido }] — el contenido lo arma la pantalla
  conteoVisible,          // cuantos expedientes se estan viendo con los filtros de ahora
  hayFiltros, onLimpiar,
  // Vistas guardadas
  vistas, filtrosActuales, onAplicarVista, onGuardarVista, onBorrarVista,
}) {
  const [pos, setPos] = useState(null);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const cajaRef = useRef(null);

  // Escritorio: colgar el panel del boton que lo abrio, sin salirse por la derecha.
  useLayoutEffect(() => {
    if (!abierto || esCelular) return;
    const el = anclaRef && anclaRef.current;
    if (!el) return;
    const recolocar = () => {
      const r = el.getBoundingClientRect();
      const ventana = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      const ancho = Math.min(ANCHO_PANEL, window.innerWidth - 24);
      const izq = Math.max(12, Math.min(r.left, window.innerWidth - ancho - 12));
      const arriba = Math.round(r.bottom + 8);
      // El alto se mide contra el FONDO DE LA VENTANA, no con un porcentaje fijo: el boton no
      // esta arriba del todo, asi que un "78% de la pantalla" colgado de el se sale por abajo y
      // el pie (con "Ver N expedientes") queda fuera de alcance. Es el mismo error de medida que
      // dejaba la barra del Kanban fuera de la pantalla.
      const abajo = Math.max(240, Math.round(ventana - arriba - 14));
      // Si arriba del boton cabe mas que debajo (boton muy abajo en la pagina), se abre HACIA
      // ARRIBA en vez de aplastarse contra el borde inferior.
      const arribaDisponible = Math.round(r.top - 14);
      if (abajo < 320 && arribaDisponible > abajo) {
        setPos({ abajoDe: Math.round(ventana - r.top + 8), left: Math.round(izq), ancho, alto: arribaDisponible });
      } else {
        setPos({ top: arriba, left: Math.round(izq), ancho, alto: abajo });
      }
    };
    recolocar();
    window.addEventListener('resize', recolocar);
    window.addEventListener('scroll', recolocar, true);
    return () => {
      window.removeEventListener('resize', recolocar);
      window.removeEventListener('scroll', recolocar, true);
    };
  }, [abierto, esCelular, anclaRef]);

  // Cerrar con Escape y con un clic fuera (patron del resto de dropdowns del proyecto).
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e) => { if (e.key === 'Escape') onCerrar(); };
    const alClicar = (e) => {
      const caja = cajaRef.current;
      const ancla = anclaRef && anclaRef.current;
      if (caja && !caja.contains(e.target) && !(ancla && ancla.contains(e.target))) onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    document.addEventListener('mousedown', alClicar);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.removeEventListener('mousedown', alClicar);
    };
  }, [abierto, onCerrar, anclaRef]);

  useEffect(() => { if (!abierto) { setNombreNuevo(''); setGuardando(false); } }, [abierto]);

  if (!abierto) return null;
  if (!esCelular && !pos) return null;

  const guardarActual = () => {
    const n = nombreNuevo.trim();
    if (!n) return;
    onGuardarVista(n);
    setNombreNuevo('');
    setGuardando(false);
  };

  const caja = (
    <div
      ref={cajaRef}
      role="dialog"
      aria-label="Filtros de expedientes"
      style={esCelular ? {
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 4300,
        background: '#fff', borderRadius: '18px 18px 0 0',
        boxShadow: '0 -12px 40px rgba(15,23,42,0.28)',
        // Hoja inferior: alto seguro (se encoge con el teclado) y columna que puede encogerse.
        maxHeight: 'calc(var(--beep-vh) * 88)',
        display: 'flex', flexDirection: 'column', fontFamily: FF,
        animation: 'beepHojaSube .22s ease-out',
      } : {
        position: 'fixed',
        ...(pos.abajoDe != null ? { bottom: pos.abajoDe } : { top: pos.top }),
        left: pos.left, width: pos.ancho, zIndex: 4300,
        background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 14,
        boxShadow: '0 18px 46px rgba(15,23,42,0.18)',
        maxHeight: pos.alto,
        display: 'flex', flexDirection: 'column', fontFamily: FF,
        animation: 'beepPanelBaja .16s ease-out',
      }}
    >
      {/* ---- CABECERA ---- */}
      <div style={{
        flexShrink: 0, padding: esCelular ? '10px 16px 12px' : '12px 16px',
        borderBottom: `1px solid ${COLOR_BORDE}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
      }}>
        {esCelular && (
          <div aria-hidden style={{
            position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)',
            width: 38, height: 4, borderRadius: 999, background: '#dfe3e8',
          }} />
        )}
        <strong style={{ fontSize: 15, color: COLOR_OSCURO, marginTop: esCelular ? 6 : 0 }}>Filtros</strong>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {hayFiltros && (
            <button type="button" onClick={onLimpiar} style={{
              background: 'transparent', border: 'none', color: COLOR_AZUL,
              fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              minHeight: 44, padding: '0 8px',
            }}>Limpiar</button>
          )}
          <button type="button" onClick={onCerrar} aria-label="Cerrar filtros" style={{
            background: 'transparent', border: 'none', color: COLOR_GRIS, cursor: 'pointer',
            width: 44, height: 44, display: 'grid', placeItems: 'center', borderRadius: 10,
          }}><X size={19} /></button>
        </div>
      </div>

      {/* ---- CUERPO (lo unico que scrollea) ----
           flex:1 + minHeight:0 para que esta region PUEDA ENCOGERSE dentro de la caja acotada.
           Sin eso crece con el contenido y empuja el pie —con el boton— fuera de la pantalla: el
           segundo defecto del bug de PERDIDA DE DATOS del 2026-09-12. */}
      <div style={{
        flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
        padding: '4px 16px 12px',
      }}>
        {grupos.filter(g => g && g.contenido).map(g => (
          <section key={g.id} style={{ padding: '12px 0', borderBottom: `1px solid ${COLOR_BORDE}` }}>
            <h3 style={{
              margin: '0 0 9px', fontSize: 11, fontWeight: 800, letterSpacing: .6,
              textTransform: 'uppercase', color: COLOR_GRIS_CL, fontFamily: FF,
            }}>{g.titulo}</h3>
            {g.contenido}
          </section>
        ))}

        {/* ---- VISTAS GUARDADAS ---- */}
        <section style={{ padding: '14px 0 4px' }}>
          <h3 style={{
            margin: '0 0 9px', fontSize: 11, fontWeight: 800, letterSpacing: .6,
            textTransform: 'uppercase', color: COLOR_GRIS_CL, fontFamily: FF,
          }}>Vistas guardadas</h3>
          {vistas.length === 0 && !guardando && (
            <p style={{ margin: '0 0 10px', fontSize: 12.5, color: COLOR_GRIS, lineHeight: 1.5 }}>
              Guarda la combinacion de filtros que uses a diario y la tendras a un clic
              (por ejemplo "Mis atorados" o "Sin cotizar +10 dias").
            </p>
          )}
          {vistas.map(v => {
            const activa = vistaActiva(v, filtrosActuales);
            return (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <button
                  type="button" onClick={() => onAplicarVista(v)}
                  style={{
                    flex: 1, minWidth: 0, textAlign: 'left', minHeight: 44, padding: '0 12px',
                    borderRadius: 9, cursor: 'pointer', fontFamily: FF, fontSize: 13.5, fontWeight: 600,
                    background: activa ? '#e8f1fa' : '#fff',
                    border: `1px solid ${activa ? COLOR_AZUL : COLOR_BORDE}`,
                    color: activa ? COLOR_AZUL : COLOR_OSCURO,
                    display: 'flex', alignItems: 'center', gap: 7,
                  }}
                >
                  {activa ? <Check size={15} strokeWidth={2.6} style={{ flexShrink: 0 }} />
                          : <Bookmark size={14} strokeWidth={2.2} style={{ flexShrink: 0, color: COLOR_GRIS_CL }} />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.nombre}</span>
                </button>
                <button
                  type="button" onClick={() => onBorrarVista(v.id)} aria-label={`Borrar la vista ${v.nombre}`}
                  style={{
                    flexShrink: 0, width: 44, height: 44, display: 'grid', placeItems: 'center',
                    background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 9,
                    color: COLOR_GRIS_CL, cursor: 'pointer',
                  }}
                ><Trash2 size={15} /></button>
              </div>
            );
          })}

          {guardando ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input
                autoFocus value={nombreNuevo}
                onChange={(e) => setNombreNuevo(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') guardarActual(); if (e.key === 'Escape') { e.stopPropagation(); setGuardando(false); } }}
                placeholder="Nombre de la vista"
                maxLength={28}
                style={{
                  flex: 1, minWidth: 0, minHeight: 44, padding: '0 12px', borderRadius: 9,
                  border: `1px solid ${COLOR_BORDE}`, fontFamily: FF, fontSize: 14,
                  color: COLOR_OSCURO, outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button type="button" onClick={guardarActual} disabled={!nombreNuevo.trim()} style={{
                flexShrink: 0, minHeight: 44, padding: '0 16px', borderRadius: 9, border: 'none',
                background: nombreNuevo.trim() ? COLOR_LIMA : '#dfe3e8', color: '#fff',
                fontFamily: FF, fontSize: 13.5, fontWeight: 700,
                cursor: nombreNuevo.trim() ? 'pointer' : 'default',
              }}>Guardar</button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setGuardando(true)}
              disabled={!hayFiltros || vistas.length >= MAX_VISTAS}
              title={!hayFiltros ? 'Pon algun filtro y podras guardar esta combinacion' : undefined}
              style={{
                marginTop: 8, minHeight: 44, padding: '0 14px', borderRadius: 9,
                border: `1px dashed ${COLOR_BORDE}`, background: '#fff',
                color: hayFiltros ? COLOR_AZUL : COLOR_GRIS_CL,
                fontFamily: FF, fontSize: 13, fontWeight: 700,
                cursor: hayFiltros ? 'pointer' : 'default',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            ><Bookmark size={14} /> Guardar estos filtros como vista</button>
          )}
        </section>
      </div>

      {/* ---- PIE: el conteo en vivo ---- */}
      <div style={{
        flexShrink: 0, padding: esCelular ? '10px 16px calc(12px + env(safe-area-inset-bottom))' : '10px 16px',
        borderTop: `1px solid ${COLOR_BORDE}`, background: '#fafbfc',
        display: 'flex', gap: 8, alignItems: 'center',
      }}>
        <button type="button" onClick={onLimpiar} disabled={!hayFiltros} style={{
          flexShrink: 0, minHeight: 44, padding: '0 14px', borderRadius: 10,
          border: `1px solid ${COLOR_BORDE}`, background: '#fff',
          color: hayFiltros ? COLOR_GRIS : COLOR_GRIS_CL,
          fontFamily: FF, fontSize: 13.5, fontWeight: 600,
          cursor: hayFiltros ? 'pointer' : 'default',
        }}>Limpiar</button>
        <button type="button" onClick={onCerrar} style={{
          flex: 1, minHeight: 44, borderRadius: 10, border: 'none', background: COLOR_AZUL,
          color: '#fff', fontFamily: FF, fontSize: 14, fontWeight: 700, cursor: 'pointer',
        }}>
          Ver {conteoVisible} {conteoVisible === 1 ? 'expediente' : 'expedientes'}
        </button>
      </div>
    </div>
  );

  return createPortal(
    <>
      <style>{`
        @keyframes beepHojaSube { from { transform: translateY(14px); opacity: .6 } to { transform: none; opacity: 1 } }
        @keyframes beepPanelBaja { from { transform: translateY(-6px); opacity: .5 } to { transform: none; opacity: 1 } }
      `}</style>
      {esCelular && (
        <div
          onClick={onCerrar}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.42)', zIndex: 4290 }}
        />
      )}
      {caja}
    </>,
    document.body
  );
}
